/**
 * LECTURE À VOIX HAUTE — mot/phrase affiché en grand (syllabes ou mots alternés en
 * couleur pour aider à "chunker"), l'enfant le lit à voix haute pendant que JARVIS
 * écoute et encourage (le micro reste ouvert en fond — voir onShow/onRead, câblés
 * par KidMode vers le fil vocal). L'avancée reste TAP-DRIVÉE (comme le quiz) pour
 * rester fiable : la voix sert à l'accompagnement, pas au score. Approche Barkley :
 * jamais de mauvaise réponse, juste du soutien jusqu'à la réussite ; une petite
 * salve (5 mots/phrases) rapporte une créature, comme les autres mini-jeux.
 */
import { useCallback, useEffect, useState } from "react";

const BURST_SIZE = 5;

type Item = { id: number; text: string; syll: string[] | null; level: number } | null;
type Drop = { emoji: string; name: string; rarity: string } | null;

export default function ReadingPractice({
  apiBase,
  onClose,
  onShow,
  onRead,
}: {
  apiBase: string;
  onClose: () => void;
  onShow: (text: string) => void;
  onRead: () => void;
}) {
  const [item, setItem] = useState<Item>(null);
  const [level, setLevel] = useState(1);
  const [burst, setBurst] = useState(0);
  const [leveledUp, setLeveledUp] = useState(false);
  const [reward, setReward] = useState<Drop>(null);
  const [showReward, setShowReward] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadNext = useCallback(async () => {
    setBusy(true);
    try {
      const r = await fetch(`${apiBase}/api/child/reading/next`);
      if (r.ok) {
        const d = await r.json();
        if (d.item) {
          setItem(d.item);
          setLevel(d.item.level);
          onShow(d.item.text);
        }
      }
    } catch { /* offline */ }
    setBusy(false);
  }, [apiBase, onShow]);

  useEffect(() => { loadNext(); }, [loadNext]);

  const markRead = async () => {
    if (!item || busy) return;
    setBusy(true);
    onRead();
    try {
      const r = await fetch(`${apiBase}/api/child/reading/done`, { method: "POST" });
      const d = await r.json();
      if (d?.leveledUp) {
        setLeveledUp(true);
        setTimeout(() => setLeveledUp(false), 2200);
      }
    } catch { /* offline */ }
    const next = burst + 1;
    if (next >= BURST_SIZE) {
      setBurst(0);
      try {
        const r2 = await fetch(`${apiBase}/api/child/minigame/reward`, { method: "POST" });
        const d2 = await r2.json();
        setReward(d2?.drop || null);
      } catch { setReward(null); }
      setShowReward(true);
      setBusy(false);
    } else {
      setBurst(next);
      await loadNext();
    }
  };

  const closeReward = () => {
    setShowReward(false);
    loadNext();
  };

  const LEVEL_LABEL: Record<number, string> = {
    1: "MOTS", 2: "PHRASES COURTES", 3: "PHRASES", 4: "PHRASES LONGUES",
  };

  return (
    <div style={S.screen}>
      <div style={S.header}>
        <button style={S.close} onClick={onClose} aria-label="Fermer">✕</button>
        <div style={S.title}>📖 LECTURE · {LEVEL_LABEL[level] || "MOTS"}</div>
        <div style={S.dots}>
          {Array.from({ length: BURST_SIZE }).map((_, i) => (
            <span key={i} style={{ ...S.dot, opacity: i < burst ? 1 : 0.25 }} />
          ))}
        </div>
      </div>

      <div style={S.boardWrap}>
        {item ? (
          <div style={S.textCard}>
            {(item.syll || item.text.split(" ")).map((chunk, i) => (
              <span key={i} style={{ color: i % 2 === 0 ? "var(--kid-cyan)" : "var(--kid-text)" }}>
                {chunk}{item.syll ? "" : " "}
              </span>
            ))}
          </div>
        ) : (
          <div style={S.textCard}>{busy ? "…" : "Rien à lire pour l'instant."}</div>
        )}
      </div>

      <div style={S.hint}>🎤 Lis-le à voix haute — JARVIS t'écoute et peut t'aider</div>

      <button style={S.doneBtn} onClick={markRead} disabled={!item || busy}>✅ J'AI LU !</button>

      {leveledUp && (
        <div style={S.toast}>🎉 Palier supérieur débloqué !</div>
      )}

      {showReward && (
        <div style={S.overlay} onClick={closeReward}>
          <div style={S.card}>
            <div style={{ fontSize: 40 }}>🏆</div>
            <div style={{ color: "var(--kid-cyan)", fontWeight: 800, fontSize: 20 }}>SALVE TERMINÉE !</div>
            {reward ? (
              <div style={S.rewardBox}>
                <div style={{ fontSize: 44 }}>{reward.emoji}</div>
                <div style={{ fontWeight: 700 }}>{reward.name}</div>
                <div style={{ color: "var(--kid-cyan)", fontSize: 12, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.14em" }}>
                  CRÉATURE DÉBLOQUÉE&nbsp;!
                </div>
              </div>
            ) : (
              <div style={S.sub}>Bien lu ! Continue pour tenter une créature.</div>
            )}
            <button style={S.doneBtn} onClick={(e) => { e.stopPropagation(); closeReward(); }}>Continuer</button>
          </div>
        </div>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  screen: { position: "fixed", inset: 0, zIndex: 62, background: "var(--kid-bg)", display: "flex", flexDirection: "column", color: "var(--kid-text)", fontFamily: "var(--kid-font-sans, system-ui)" },
  header: { display: "flex", alignItems: "center", gap: 12, padding: "16px 16px 8px", borderBottom: "1px solid var(--kid-border)" },
  close: { width: 40, height: 40, borderRadius: 12, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-cyan)", fontSize: 18, cursor: "pointer" },
  title: { flex: 1, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.1em", fontSize: 13, color: "var(--kid-cyan)" },
  dots: { display: "flex", gap: 5 },
  dot: { width: 8, height: 8, borderRadius: 999, background: "var(--kid-cyan)" },
  boardWrap: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  textCard: { textAlign: "center", fontSize: "clamp(28px, 8vw, 52px)", fontWeight: 800, lineHeight: 1.3, maxWidth: 560 },
  hint: { textAlign: "center", color: "var(--kid-text-3)", fontSize: 12, padding: "0 16px 4px" },
  doneBtn: { display: "block", width: "calc(100% - 32px)", margin: "0 16px calc(env(safe-area-inset-bottom) + 16px)", padding: "15px", borderRadius: 14, border: "none", background: "linear-gradient(180deg, var(--kid-cyan), #008cbe)", color: "#04222c", fontSize: 16, fontWeight: 800, cursor: "pointer" },
  toast: { position: "absolute", top: "18%", left: "50%", transform: "translateX(-50%)", background: "rgba(6,9,15,0.9)", border: "1px solid var(--kid-cyan)", color: "var(--kid-cyan)", padding: "10px 18px", borderRadius: 999, fontSize: 14, fontWeight: 700, zIndex: 65 },
  overlay: { position: "fixed", inset: 0, zIndex: 66, background: "rgba(3,5,9,0.82)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  card: { textAlign: "center", padding: "26px 30px", borderRadius: 20, border: "1px solid var(--kid-cyan-40)", background: "var(--kid-bg)", maxWidth: 340, width: "100%" },
  sub: { color: "var(--kid-text-3)", fontSize: 13, marginTop: 6 },
  rewardBox: { margin: "16px auto 4px", padding: "14px", borderRadius: 16, border: "2px solid var(--kid-cyan)", display: "flex", flexDirection: "column", gap: 4, alignItems: "center" },
};
